class FreeShippingBanner {
  constructor() {
    this.banner = document.querySelector('.free-shipping-banner-section[data-goal]');
    if (!this.banner) return;
    
    this.textElement = this.banner.querySelector('.free-shipping-banner__text');
    if (!this.textElement) return;
    
    this.controller = new AbortController();
    this.settings = this.parseSettings();
    this.lastContent = '';
    
    // Variables pour le polling
    this.lastKnownQuantity = 0;
    this.lastKnownSubtotal = 0;
    
    this.init();
  }

  parseSettings() {
    const data = this.banner.dataset;
    return {
      goalType: data.goalType || 'subtotal',
      goal: parseFloat(data.goal) || 65,
      title: data.title || 'Plus que [amount] pour la livraison gratuite !',
      successMessage: data.successMessage || 'Félicitations ! Livraison gratuite obtenue !',
      showWhenReached: data.showWhenReached !== 'false'
    };
  }

  init() {
    this.bindEvents();
    this.update();
    
    // Polling de sécurité pour s'assurer que la bannière reste synchronisée
    this.startPolling();
  }
  
  startPolling() {
    // Vérifier toutes les 2 secondes si le panier a changé
    this.pollingInterval = setInterval(() => {
      this.checkCartChanges();
    }, 2000);
    
    // Cleanup polling
    this.controller.signal.addEventListener('abort', () => {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
      }
    });
  }
  
  checkCartChanges() {
    // Récupérer la valeur actuelle du panier
    const currentQuantity = this.getCartQuantity();
    const currentSubtotal = this.getCartSubtotal();
    
    // Comparer avec les valeurs précédentes
    if (this.lastKnownQuantity !== currentQuantity || this.lastKnownSubtotal !== currentSubtotal) {
      this.lastKnownQuantity = currentQuantity;
      this.lastKnownSubtotal = currentSubtotal;
      this.scheduleUpdate(0);
    }
  }

  bindEvents() {
    const options = { signal: this.controller.signal };
    
    // Écouter tous les événements de quantité
    document.addEventListener('input', this.handleQuantityChange.bind(this), options);
    document.addEventListener('change', this.handleQuantityChange.bind(this), options);
    document.addEventListener('click', this.handleCartActions.bind(this), options);
    
    // Événements Shopify
    document.addEventListener('cart:updated', this.debounce(this.update.bind(this), 100), options);
    document.addEventListener('cart:changed', this.debounce(this.update.bind(this), 100), options);
    
    // Événements spécifiques aux composants web du thème
    document.addEventListener('quantity:changed', this.scheduleUpdate.bind(this), options);
    document.addEventListener('cart:item-updated', this.scheduleUpdate.bind(this), options);
    
    // Observer pour les mutations DOM du panier
    this.observeCartChanges();
  }

  handleQuantityChange(event) {
    // Sélecteurs spécifiques à ce thème
    if (event.target.matches('.quantity__input, input[name="updates[]"], [data-quantity-variant-id]')) {
      this.scheduleUpdate(100);
    }
  }

  handleCartActions(event) {
    // Boutons de quantité spécifiques à ce thème
    if (event.target.matches('.quantity__button[name="plus"], .quantity__button[name="minus"]')) {
      this.scheduleUpdate(200);
    }
    
    // Boutons de suppression (composant web custom)
    if (event.target.closest('cart-remove-button')) {
      this.scheduleUpdate(300);
    }
  }

  observeCartChanges() {
    // Observer pour le panier principal et cart drawer
    const cartSelectors = [
      '.cart-items', 
      '.cart__contents', 
      '.cart-drawer__inner',
      '[data-cart-items]',
      '.main-cart'
    ];
    
    cartSelectors.forEach(selector => {
      const container = document.querySelector(selector);
      if (container) {
        const observer = new MutationObserver((mutations) => {
          const hasRelevantChange = mutations.some(mutation => {
            // Changements dans la structure (ajout/suppression d'items)
            if (mutation.type === 'childList') return true;
            
            // Changements de valeur des inputs
            if (mutation.type === 'attributes' && 
                ['value', 'data-quantity-variant-id'].includes(mutation.attributeName)) {
              return true;
            }
            
            return false;
          });
          
          if (hasRelevantChange) {
            this.scheduleUpdate(50);
          }
        });
        
        observer.observe(container, { 
          childList: true, 
          subtree: true, 
          attributes: true,
          attributeFilter: ['value', 'data-quantity-variant-id', 'data-cart-quantity']
        });
        
        // Cleanup observer
        this.controller.signal.addEventListener('abort', () => observer.disconnect());
      }
    });
    
    // Observer pour le compteur du panier (fallback)
    const cartCount = document.querySelector('.cart-count, [data-cart-count], .cart__quantity');
    if (cartCount) {
      const countObserver = new MutationObserver(() => this.scheduleUpdate(100));
      countObserver.observe(cartCount, { 
        childList: true, 
        characterData: true, 
        subtree: true 
      });
      this.controller.signal.addEventListener('abort', () => countObserver.disconnect());
    }
  }

  scheduleUpdate(delay = 50) {
    clearTimeout(this.updateTimer);
    this.updateTimer = setTimeout(() => this.update(), delay);
  }

  async update() {
    try {
      const response = await fetch('/cart.js', { 
        signal: this.controller.signal 
      });
      const cart = await response.json();
      this.updateBanner(cart);
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.updateFromDOM();
      }
    }
  }

  updateBanner(cart) {
    const isQuantity = this.settings.goalType === 'quantity';
    
    // Calculer la valeur actuelle sans les articles cachés
    let currentValue = isQuantity ? cart.item_count : cart.items_subtotal_price;
    
    cart.items?.forEach(item => {
      if (item.product_tags?.includes('cart-hidden')) {
        currentValue -= isQuantity ? item.quantity : item.line_price;
      }
    });

    const goalInCorrectUnit = isQuantity ? this.settings.goal : this.settings.goal * 100;
    const goalReached = currentValue >= goalInCorrectUnit;
    const remaining = Math.max(goalInCorrectUnit - currentValue, 0);
    
    this.renderBanner(goalReached, remaining, isQuantity);
  }

  updateFromDOM() {
    const isQuantity = this.settings.goalType === 'quantity';
    const currentValue = isQuantity ? this.getCartQuantity() : this.getCartSubtotal();
    const goalInCorrectUnit = isQuantity ? this.settings.goal : this.settings.goal * 100;
    const goalReached = currentValue >= goalInCorrectUnit;
    const remaining = Math.max(goalInCorrectUnit - currentValue, 0);
    
    this.renderBanner(goalReached, remaining, isQuantity);
  }

  renderBanner(goalReached, remaining, isQuantity) {
    requestAnimationFrame(() => {
      // Gérer la visibilité
      if (goalReached && !this.settings.showWhenReached) {
        this.banner.style.display = 'none';
        return;
      }
      this.banner.style.display = 'block';

      // Construire le contenu
      const content = goalReached ? 
        this.settings.successMessage : 
        this.buildProgressText(remaining, isQuantity);
      
      // Mettre à jour seulement si le contenu a changé
      if (content !== this.lastContent) {
        this.textElement.innerHTML = content;
        this.lastContent = content;
      }

      // Gérer les classes CSS
      const bannerEl = this.banner.querySelector('.free-shipping-banner');
      if (bannerEl) {
        bannerEl.classList.toggle('free-shipping-banner--success', goalReached);
      }
    });
  }

  buildProgressText(remaining, isQuantity) {
    const formattedAmount = isQuantity ? 
      Math.ceil(remaining).toString() : 
      this.formatMoney(remaining);
    
    let text = this.settings.title;
    
    // Support pour [amount] et {amount}
    if (text.includes('[amount]') || text.includes('{amount}')) {
      if (remaining > 0) {
        const replacement = `<span class="free-shipping-banner__amount">${formattedAmount}</span>`;
        text = text.replace(/\[amount\]|\{amount\}/g, replacement);
      } else {
        text = text.replace(/\[amount\]|\{amount\}/g, '');
      }
    } else if (remaining > 0) {
      text += ` <span class="free-shipping-banner__amount">${formattedAmount}</span>`;
    }
    
    return text;
  }

  getCartQuantity() {
    const quantityEl = document.querySelector('[data-cart-quantity], .cart-count');
    if (quantityEl) {
      return parseInt(quantityEl.textContent || quantityEl.dataset.cartQuantity) || 0;
    }
    
    // Fallback: sommer les inputs de quantité
    const inputs = document.querySelectorAll('input[name="updates[]"], .quantity__input');
    return Array.from(inputs).reduce((sum, input) => sum + (parseInt(input.value) || 0), 0);
  }

  getCartSubtotal() {
    const subtotalEl = document.querySelector('[data-cart-subtotal], .cart__subtotal .price');
    if (subtotalEl) {
      const text = subtotalEl.textContent || subtotalEl.dataset.cartSubtotal;
      return this.parseMoney(text);
    }
    return 0;
  }

  parseMoney(text) {
    if (!text) return 0;
    const match = text.match(/[\d\s,.-]+/);
    if (!match) return 0;
    
    const cleanPrice = match[0].replace(/\s/g, '').replace(',', '.');
    return Math.round(parseFloat(cleanPrice) * 100) || 0;
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(cents / 100);
  }

  debounce(func, delay) {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(this, args), delay);
    };
  }

  destroy() {
    this.controller.abort();
    clearTimeout(this.updateTimer);
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
  }
}

// Initialisation
function initBanner() {
  if (document.querySelector('.free-shipping-banner-section[data-goal]')) {
    window.freeShippingBanner = new FreeShippingBanner();
  }
}

// Attendre le chargement complet
if (document.readyState === 'complete') {
  initBanner();
} else {
  window.addEventListener('load', initBanner);
}

// Réinitialiser lors du chargement de sections Shopify
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('.free-shipping-banner-section')) {
    window.freeShippingBanner?.destroy();
    setTimeout(initBanner, 50);
  }
});

// Cleanup
window.addEventListener('beforeunload', () => {
  window.freeShippingBanner?.destroy();
}); 